'use client'

import { alert } from '@repo/ui/Alert'
import { ClientMatterDetailsPill } from '@repo/ui/ClientMatterDetailsPill'
import { InfoIcon } from '@repo/ui/InfoIcon'
import { MatterStatusPill } from '@repo/ui/MatterStatusPill'
import { Pill } from '@repo/ui/Pill'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useClientStore } from '@repo/store/useClientStore'
import { useEffect, useState } from 'react'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useNavigate } from 'react-router-dom'
import { useRouteLoaderData } from 'react-router-dom'
import { useUndertakingsStore } from '@repo/store/useUndertakingsStore'
import { useUrlStore } from '@repo/store/useUrlStore'
import { useWindowSize } from '@uidotdev/usehooks'
import MUIButton from '@mui/material/Button'
import MUIIconButton from '@mui/material/IconButton'
import MUIStack from '@mui/material/Stack'
import MUITooltip from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const PageTitleBlock = () => {
  const { breadcrumbs, isHome, routeId, path, iframeSrc, iframeURL, getLastUrlSegment } = useUrlStore()
  const { currentMatter, fetchMatter } = useMattersStore()
  const { currentClient } = useClientStore()
  const { clientNo, matterNo, matterDescription, prospect, workflowTypeDescription } = currentMatter || {}
  const { highRisk } = currentClient || {}
  const { pageDescription, pageIsMatterSpecific, pageIsClientSpecific, pageTitleOverride } =
    useRouteLoaderData(routeId) || {}
  const { isLoggedIn } = useAuthenticationStore()
  const { pageTitleBlockIsVisible, setPageTitleBlockIsVisible } = useGlobalSettingsStore()
  const { fetchMatterUndertakingCount, isUndertakingsSupported, undertakingsCounts } = useUndertakingsStore()
  const { height } = useWindowSize()

  const navigate = useNavigate()

  const [matterHasOutstandingUndertakings, setMatterHasOutstandingUndertakings] = useState(false)
  const [newHighRisk, setNewHighRisk] = useState(highRisk)
  const [newMatterDescription, setNewMatterDescription] = useState(matterDescription)
  const [newProspect, setNewProspect] = useState(prospect)
  const [newWorkflowTypeDescription, setNewWorkflowTypeDescription] = useState(workflowTypeDescription)
  const [undertakingsDischargeCount, setUndertakingsDischargeCount] = useState(0)
  const [undertakingsDischargeNotPossibleCount, setUndertakingsDischargeNotPossibleCount] = useState(0)
  const [undertakingsOutstandingCount, setUndertakingsOutstandingCount] = useState(0)
  const [undertakingsTotalCount, setUndertakingsTotalCount] = useState(0)

  useEffect(() => {
    // update matter data when route changes - e.g. if matter is archived or high risk
    // this call is debounced in the store
    if (isLoggedIn && !isHome && pageIsMatterSpecific && clientNo && matterNo) {
      fetchMatter({ clientMatterNo: `${clientNo}-${matterNo}`, trackEvent: true })
    }
  }, [path, iframeSrc, iframeURL, pageIsMatterSpecific, fetchMatter, clientNo, matterNo, isHome, isLoggedIn])

  useEffect(() => {
    // don't show the PageTitleBlock if on reduced-height screen
    // we're doing this with JS rather than CSS, because we want to store this value for use by other components
    const breakpoint = 16 * 62 // i.e. 62rem
    setPageTitleBlockIsVisible(height >= breakpoint)
  }, [height, setPageTitleBlockIsVisible])

  useEffect(() => {
    setNewHighRisk(highRisk)
    setNewProspect(prospect)
    setNewWorkflowTypeDescription(workflowTypeDescription)
    setNewMatterDescription(matterDescription)
  }, [highRisk, prospect, workflowTypeDescription, matterDescription])

  useEffect(() => {
    if (!isUndertakingsSupported || !pageTitleBlockIsVisible || isHome || !clientNo) return

    fetchMatterUndertakingCount({
      clientNo: clientNo,
      matterNo: matterNo
    })
  }, [isUndertakingsSupported, pageTitleBlockIsVisible, fetchMatterUndertakingCount, clientNo, matterNo, isHome])

  useEffect(() => {
    if (matterNo && undertakingsCounts) {
      const matchingCount = undertakingsCounts.find(
        (count) => count.clientNo === clientNo && count.matterNo === matterNo
      )

      if (matchingCount) {
        setMatterHasOutstandingUndertakings(matchingCount.matterHasOutstandingUndertakings)
        setUndertakingsDischargeCount(matchingCount.dischargedCount || 0)
        setUndertakingsDischargeNotPossibleCount(matchingCount.dischargeNotPossibleCount || 0)
        setUndertakingsOutstandingCount(matchingCount.outstandingCount || 0)
        setUndertakingsTotalCount(matchingCount.totalCount || 0)
      } else {
        setMatterHasOutstandingUndertakings(false)
        setUndertakingsDischargeCount(0)
        setUndertakingsDischargeNotPossibleCount(0)
        setUndertakingsOutstandingCount(0)
        setUndertakingsTotalCount(0)
      }
    }
  }, [undertakingsCounts, clientNo, matterNo])

  useEffect(() => {
    const matchingCount = undertakingsCounts.find((count) => count.clientNo === clientNo && count.matterNo === matterNo)

    if (
      path.includes('case-management/matters/matter-archive') &&
      matchingCount?.matterHasOutstandingUndertakings &&
      clientNo &&
      matterNo
    ) {
      alert(
        'This matter has outstanding undertakings. It should not be archived until all undertakings have been discharged.'
      )
    }
  }, [path, isUndertakingsSupported, navigate, clientNo, matterNo, undertakingsCounts])

  if (isHome) return null

  if (pageIsMatterSpecific && currentMatter?.isUserRestricted) {
    throw new Error('You are not authorised to view this matter', {
      cause: { status: 403 }
    })
  }

  const { lastUrlSegmentLabel, lastUrlSegment } = getLastUrlSegment()

  const lastBreadcrumbItem = breadcrumbs[breadcrumbs.length - 1]

  const pageTitle =
    lastBreadcrumbItem && lastBreadcrumbItem.urlSegment === lastUrlSegment
      ? lastBreadcrumbItem?.label || ''
      : lastUrlSegmentLabel
  const routeTitle = pageTitle ? '' : routeId?.split('/')[0].split('-').join(' ')
  const routeTitleSentenceCase = routeTitle ? `${routeTitle.charAt(0).toUpperCase()}${routeTitle.slice(1)}` : ''

  return (
    <MUIStack
      data-testid="PageTitleBlock"
      direction="column"
      sx={{
        mt: '7rem',
        mb: '1rem',
        padding: '16px 70px 8px',
        position: 'relative',
        display: pageTitleBlockIsVisible ? 'flex' : 'none'
      }}
    >
      <MUITypography variant="h1" sx={{ color: 'support.navy', mt: '43px', mb: '0px' }}>
        {pageTitleOverride || pageTitle || routeTitleSentenceCase || 'Page'}{' '}
        <TooltipInfoIcon content={pageDescription} />
      </MUITypography>

      <MUIStack
        direction="row"
        alignItems="center"
        spacing={4}
        sx={{ height: '22px', mt: '10px', position: 'relative', pointerEvents: 'auto' }}
      >
        {(pageIsMatterSpecific || pageIsClientSpecific) && clientNo && matterNo ? (
          <>
            <ClientMatterDetailsPill
              clientNo={clientNo}
              matterNo={matterNo}
              sx={{ marginTop: '5px' }}
              isUserRestricted={currentMatter?.isUserRestricted}
            />

            <MatterStatusPill matter={currentMatter} />
            {newHighRisk ? <Pill label="High risk" color="white" backgroundColor="error.main" /> : null}
            {newProspect ? <Pill label="Prospect" backgroundColor="support.sand" /> : null}
            {isUndertakingsSupported && undertakingsTotalCount > 0 ? (
              <MUITooltip
                placement="bottom"
                arrow
                title={
                  <MUIStack
                    display="flex"
                    direction="column"
                    justifyContent="center"
                    alignItems="flex-start"
                    height="auto"
                    width="100%"
                    gap={1}
                    sx={{ cursor: 'pointer', p: 2 }}
                  >
                    <MUITypography
                      variant="h6"
                      sx={{
                        m: 0,
                        p: 0,
                        color: 'white',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      TOTAL: {undertakingsTotalCount}
                    </MUITypography>
                    <MUITypography
                      variant="body2"
                      sx={{
                        m: 0,
                        p: 0,
                        color: 'white',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Discharged: {undertakingsDischargeCount}
                    </MUITypography>
                    <MUITypography
                      variant="body2"
                      sx={{
                        m: 0,
                        p: 0,
                        color: 'white',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Outstanding: {undertakingsOutstandingCount}
                    </MUITypography>
                    <MUITypography
                      variant="body2"
                      sx={{
                        m: 0,
                        p: 0,
                        color: 'white',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Not possible: {undertakingsDischargeNotPossibleCount}
                    </MUITypography>
                  </MUIStack>
                }
              >
                <MUIButton
                  onClick={() => navigate('/case-management/matters/undertakings')}
                  variant="text"
                  sx={{ minWidth: '0', padding: '0', textTransform: 'unset' }}
                >
                  <Pill
                    label={!matterHasOutstandingUndertakings ? 'Undertakings discharged' : 'Outstanding undertakings'}
                    backgroundColor={!matterHasOutstandingUndertakings ? 'success.main' : 'warning.light'}
                    color="white"
                  />
                </MUIButton>
              </MUITooltip>
            ) : null}

            {newWorkflowTypeDescription ? (
              <Pill label={newWorkflowTypeDescription} backgroundColor="background.mid" />
            ) : null}

            {newMatterDescription ? (
              <MUITooltip title={newMatterDescription.length > 60 ? newMatterDescription : null} placement="top" arrow>
                <MUITypography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    mb: 0,
                    minHeight: '22px',
                    flex: 1,
                    pr: pageIsMatterSpecific || pageIsClientSpecific ? '45px' : 0
                  }}
                  noWrap
                >
                  {newMatterDescription}
                </MUITypography>
              </MUITooltip>
            ) : null}
          </>
        ) : null}
      </MUIStack>
    </MUIStack>
  )
}

const TooltipInfoIcon = ({ content }) => {
  if (!content) return null
  return (
    <MUITooltip title={content} placement="right" arrow>
      <MUIIconButton>
        <InfoIcon sx={{ color: 'black', opacity: 0.56, fontSize: '20px', width: '20px' }} />
      </MUIIconButton>
    </MUITooltip>
  )
}

TooltipInfoIcon.propTypes = {
  content: PropTypes.string
}
